import React, {useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner, faFileExcel} from '@fortawesome/free-solid-svg-icons';
import {useTranslation} from 'react-i18next';
import {exportExpensesToCSV, getExportSummary} from '../../utils/exportHelpers';
import {Expense, SponsorExpense} from '../../types';

interface ExportButtonProps {
    expenses: (Expense | SponsorExpense)[];
    participants: Record<string, string>;
    eventName: string;
}

const ExportButton: React.FC<ExportButtonProps> = ({
                                                       expenses,
                                                       participants,
                                                       eventName
                                                   }) => {
    const {t} = useTranslation();
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = async () => {
        if (expenses.length === 0) {
            alert(t('event.export.noExpenses'));
            return;
        }

        setIsExporting(true);

        try {
            // Small delay to show loading state
            await new Promise(resolve => setTimeout(resolve, 500));

            // Export expenses to CSV
            exportExpensesToCSV(expenses, participants, eventName, t);

        } catch (error) {
            console.error('Export error:', error);
            alert(t('event.export.exportFailed'));
        } finally {
            setIsExporting(false);
        }
    };

    const exportSummary = getExportSummary(expenses);

    return (
        <button
            onClick={handleExport}
            disabled={isExporting || expenses.length === 0}
            className="inline-flex items-center px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            title={expenses.length === 0 ? t('event.export.noExpenses') : t('event.export.downloadCSV')}
            aria-label={expenses.length === 0 ? t('event.export.noExpenses') : t('event.export.downloadCSV')}
            aria-disabled={isExporting || expenses.length === 0}
        >
            {isExporting ? (
                <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" aria-hidden="true"/>
                    <span>{t('event.export.exporting')}</span>
                </>
            ) : (
                <>
                    <FontAwesomeIcon icon={faFileExcel} className="mr-2" aria-hidden="true"/>
                    <span className="hidden sm:inline">{t('event.export.exportCSV')}</span>
                    <span className="sm:hidden">{t('event.export.export')}</span>
                    {expenses.length > 0 && (
                        <span className="ml-1 text-xs bg-green-800 px-1.5 py-0.5 rounded-full" aria-label={`${exportSummary.totalExpenses} expenses`}>
                            {exportSummary.totalExpenses}
                        </span>
                    )}
                </>
            )}
        </button>
    );
};

export default ExportButton;